import dayjs from 'dayjs';

/**
 * Diagnósticos pela CID-10 (versão do DATASUS) ou pela CID-11 (ICD-11 MMS da
 * OMS). Os códigos e títulos vêm das tabelas oficiais gravadas no servidor
 * (system_icd10_codes e system_icd11_codes); o front nunca inventa nem altera
 * um código, só busca e grava o escolhido com a classificação usada.
 */

export const ICD10_ATTRIBUTION = 'CID-10, Organização Mundial da Saúde, versão em português do DATASUS (Ministério da Saúde).';

/** Atribuição exigida pela licença CC BY-ND 3.0 IGO da OMS. */
export const ICD11_ATTRIBUTION = 'CID-11 (ICD-11 MMS), Organização Mundial da Saúde, licença CC BY-ND 3.0 IGO.';

/**
 * Classificações aceitas, na ordem do seletor. A CID-10 vem primeiro porque é
 * a que o Brasil ainda usa em atestados, convênios e no SUS.
 */
export const CLASSIFICATIONS = [
  { key: 'cid10', label: 'CID-10', placeholder: 'Código ou nome, por exemplo F32.1 ou enxaqueca', attribution: ICD10_ATTRIBUTION },
  { key: 'cid11', label: 'CID-11', placeholder: 'Código ou nome, por exemplo 8A80 ou enxaqueca', attribution: ICD11_ATTRIBUTION },
];

export const DEFAULT_CLASSIFICATION = 'cid10';

/** Classificação de um diagnóstico (ou de uma chave); sem a coluna, é CID-11 (registros de antes da CID-10). */
export const classificationOf = (value) => {
  const key = typeof value === 'string' ? value : value?.classification;
  return CLASSIFICATIONS.find((item) => item.key === key) || CLASSIFICATIONS[1];
};

/** Atribuições das classificações presentes na lista (mais `extraKey`, se vier). */
export const attributionsFor = (list, extraKey) => {
  const keys = new Set((Array.isArray(list) ? list : []).map((item) => classificationOf(item).key));
  if (extraKey) keys.add(extraKey);
  return CLASSIFICATIONS.filter((item) => keys.has(item.key)).map((item) => item.attribution);
};

export const MAX_PER_APPOINTMENT = 20;
export const MIN_QUERY_LENGTH = 2;

const isSame = (a, b) => a != null && b != null && String(a) === String(b);

export const isFromAppointment = (diagnosis, appointmentId) => isSame(diagnosis.appointmentId, appointmentId);

/** Diagnósticos de um atendimento (lista vazia sem atendimento). */
export const diagnosesOfAppointment = (list, appointmentId) =>
  appointmentId != null ? (Array.isArray(list) ? list : []).filter((item) => isFromAppointment(item, appointmentId)) : [];

/**
 * Códigos para documentos (atestado, declaração), só o código, agrupados por
 * classificação: "CID-10: F32.1, G43.9". Vazio sem diagnóstico.
 */
export function cidCodesText(list) {
  const items = Array.isArray(list) ? list : [];
  return CLASSIFICATIONS
    .map((classification) => {
      const codes = items.filter((item) => classificationOf(item).key === classification.key).map((item) => item.code);
      return codes.length > 0 ? `${classification.label}: ${codes.join(', ')}` : '';
    })
    .filter(Boolean)
    .join('; ');
}

/**
 * Agrupa por atendimento, do mais recente para o mais antigo:
 * `[{ appointmentId, date, items }]`, `date` em DD/MM/YYYY (data do registro).
 */
export function groupByAppointment(list) {
  const groups = new Map();
  (Array.isArray(list) ? list : []).forEach((diagnosis) => {
    const key = String(diagnosis.appointmentId);
    if (!groups.has(key)) groups.set(key, { appointmentId: diagnosis.appointmentId, createdAt: diagnosis.createdAt, items: [] });
    const group = groups.get(key);
    group.items.push(diagnosis);
    if (diagnosis.createdAt < group.createdAt) group.createdAt = diagnosis.createdAt;
  });
  return [...groups.values()]
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
    .map((group) => ({ ...group, date: group.createdAt ? dayjs(group.createdAt).format('DD/MM/YYYY') : '' }));
}
